import { useEffect, useRef, useState } from "react";

/**
 * Tracks current visible PDF page inside a scrollable container.
 * Works accurately even with 100+ pages, variable page heights, and spacing.
 */
export const usePdfScrollTracker = (previewContainerRef, images, scale = 1, rotateAngle) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageOffsets, setPageOffsets] = useState([]);
  const scrollTimeout = useRef(null);
  const isManualScroll = useRef(false); // <-- flag to prevent auto override

  // Record page offset positions after render
  useEffect(() => {
    const container = previewContainerRef.current;
    if (!container) return;
  
    const updateOffsets = () => {
      const pageEls = Array.from(container.querySelectorAll(".pdf-viewer-item"));
      if (pageEls.length === images.length) {
        const positions = pageEls.map((el) => el.offsetTop);
        setPageOffsets(positions);
      }
    };
  
    const observer = new MutationObserver(() => updateOffsets());
    observer.observe(container, { childList: true, subtree: true });

    const resizeObserver = new ResizeObserver(updateOffsets);
    resizeObserver.observe(container);
    Array.from(container.querySelectorAll(".pdf-viewer-item")).forEach((el) =>
      resizeObserver.observe(el)
    );
  
    // Initial run
    setTimeout(updateOffsets, 100);

  
    return () => {
        observer.disconnect();
        resizeObserver.disconnect();
    }
  }, [images, scale, rotateAngle]);

useEffect(() => {
    if (!previewContainerRef.current || !pageOffsets.length) return;
    const container = previewContainerRef.current;

    const handleScroll = () => {
      if (isManualScroll.current) return;
      const scaledScrollTop = container.scrollTop / scale; // <-- adjust scroll for zoom
      const scaledScrollBottom = scaledScrollTop + container.clientHeight / scale;
      const totalHeight = container.scrollHeight / scale;

      // Handle last page edge
      if (scaledScrollBottom >= totalHeight - 50 / scale) {
        setCurrentPage(pageOffsets.length);
        return;
      }

      const page = findCurrentPage(scaledScrollTop, pageOffsets);
      setCurrentPage(page);
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [pageOffsets, scale]);

  const scrollToPage = (pageNum) => {
    const container = previewContainerRef.current;
    if (!container || !pageOffsets.length) return;

    const top = pageOffsets[pageNum - 1] ?? 0;
    isManualScroll.current = true;
    setCurrentPage(pageNum);
    container.scrollTo({ top: top * scale, behavior: "smooth" });

    // Re-enable scroll tracking after scroll completes
    setTimeout(() => {
      isManualScroll.current = false;
    }, 500);
  };

  return [currentPage, scrollToPage];
};

// Binary search for current visible page
function findCurrentPage(scrollTop, offsets) {
  let low = 0,
    high = offsets.length - 1;
  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    if (
      offsets[mid] <= scrollTop &&
      (mid === offsets.length - 1 || offsets[mid + 1] > scrollTop)
    ) {
      return mid + 1;
    } else if (offsets[mid] > scrollTop) {
      high = mid - 1;
    } else {
      low = mid + 1;
    }
  }
  return 1;
}

